import React, { useState, useRef, ReactNode } from 'react';
import { LoaderCircle, ArrowDown } from 'lucide-react';

interface PullToRefreshProps {
  onRefresh: () => Promise<void> | void;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
}

export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  children,
  disabled = false,
  className = '',
  scrollRef
}) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const isDragging = useRef(false);
  const internalRef = useRef<HTMLDivElement>(null);

  const PULL_THRESHOLD = 70; // Distance in px to trigger refresh
  const MAX_PULL = 120;      // Max pull limit

  const getContainer = () => scrollRef?.current || internalRef.current;

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (disabled || isRefreshing) return;
    const container = getContainer();
    if (container && container.scrollTop <= 2) {
      touchStartY.current = e.touches[0].clientY;
      isDragging.current = true;
    } else {
      isDragging.current = false;
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging.current || disabled || isRefreshing) return;
    const container = getContainer();
    if (container && container.scrollTop > 2) {
      isDragging.current = false;
      setPullDistance(0);
      return;
    }

    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY.current;

    if (diff > 0) {
      // Smooth resistance curve
      const dampedDistance = Math.min(MAX_PULL, diff * 0.45);
      setPullDistance(dampedDistance);
    } else {
      setPullDistance(0);
    }
  };

  const handleTouchEnd = async () => {
    if (!isDragging.current) return;
    isDragging.current = false;

    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(50); // Keep spinner visible during refresh
      try {
        await onRefresh();
      } catch (err) {
        console.warn('Pull to refresh error:', err);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  };

  return (
    <div
      ref={internalRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      className={`relative ${className}`}
    >
      {/* Animated Refresh Header Indicator */}
      <div
        style={{
          height: `${pullDistance}px`,
          transition: isDragging.current ? 'none' : 'height 0.25s cubic-bezier(0.2, 0.8, 0.2, 1)'
        }}
        className="w-full flex items-center justify-center overflow-hidden pointer-events-none z-30"
      >
        <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/95 shadow-md border border-slate-200 text-slate-700 text-xs font-bold backdrop-blur-md">
          {isRefreshing ? (
            <>
              <LoaderCircle className="w-4 h-4 animate-spin text-blue-900" />
              <span>Updating feed…</span>
            </>
          ) : pullDistance >= PULL_THRESHOLD ? (
            <>
              <ArrowDown className="w-4 h-4 rotate-180 transition-transform duration-200 text-blue-900" />
              <span className="text-blue-900 font-extrabold">Release to refresh</span>
            </>
          ) : (
            <>
              <ArrowDown className="w-4 h-4 text-slate-400" />
              <span className="text-slate-500">Pull down to refresh</span>
            </>
          )}
        </div>
      </div>

      {children}
    </div>
  );
};
